import { DropdownItem } from 'components/dropdowns/base/base';
import { MultiSelectDropdown } from 'components/dropdowns/multiselect/multiselect';
import { SelectDropdown } from 'components/dropdowns/select/select';
import { TextEditorConfig } from 'components/editors/text/text';
import { wccModules } from 'enums/wccModules';
import { inject, injectable } from 'inversify';
import { Observable, Subscribable, SubscribableOrNullableValue } from 'knockout';
import SimpleTopicsManager from 'managers/discussion/simpleTopics';
import { IWCCStorageManager } from 'managers/iStorage';
import { withEffect } from 'mixins/withEffect';
import { EffectsContainer } from 'mixins/withEffects';
import { SimpleTopic } from 'models/simpleTopic';
import { SimpleTopicsGroup } from 'models/simpleTopicsGroup';
import CommunityNavigationWidgetLinkType from '../enums/types';
import { CommunityNavigationWidgetLinkModel } from '../link';
import './link.html';

const { discussionId } = settings;

export interface CommunityNavigationWidgetLayoutLinkVMConfig {
    link: CommunityNavigationWidgetLinkModel

    public?: SubscribableOrNullableValue<boolean>
    showErrors?: SubscribableOrNullableValue<boolean>

    onRemove?: Action<[CommunityNavigationWidgetLinkModel]>
    onMove?: Action<[CommunityNavigationWidgetLinkModel, number]>
}

@injectable()
export default class CommunityNavigationWidgetLayoutLinkVM {
    private link: CommunityNavigationWidgetLinkModel
    private topicId: Observable<string | undefined>
    private type: Observable<CommunityNavigationWidgetLinkType>

    private hasTopics: Subscribable<boolean>

    pickedGroup = ko.observable<SimpleTopicsGroup | undefined>();
    pickedTopic = ko.observable<SimpleTopic | undefined>();

    accessSelect: MultiSelectDropdown<number>
    typeSelect: SelectDropdown<number>

    cssEditor: TextEditorConfig
    titleEditor: TextEditorConfig
    routeEditor: TextEditorConfig

    css: Observable<string>
    title: Observable<string>
    route: Observable<string | undefined>    

    topics: Subscribable<Array<SimpleTopic>>
    groups: Subscribable<Array<SimpleTopicsGroup>>

    isPublic: Subscribable<boolean>
    showErrors: Subscribable<boolean>
    showRouteEditor: Subscribable<boolean>
    showTopicGroupsList: Subscribable<boolean>
    showTopicsList: Subscribable<boolean>
    hasGroups: Subscribable<boolean>
    hasNoGroups: Subscribable<boolean>
    allowToSetAccess: Subscribable<boolean>

    constructor(
        @inject(wccModules.componentConfig) private config: CommunityNavigationWidgetLayoutLinkVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.storage) storage: IWCCStorageManager,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        this.link = config.link;
        this.isPublic = ko.flattenComputed(config.public, true);
        this.showErrors = ko.flattenComputed(config.showErrors, false);

        this.css = ko.pureComputed({ read: this.link.css, write: this.link.css }).extend({ validate: () => this.link.validateCSS() });
        this.title = ko.pureComputed({ read: this.link.title, write: this.link.title }).extend({ validate: () => this.link.validateTitle() });
        this.type = this.link.type;
        this.route = ko.pureComputed({ read: this.link.route, write: this.link.route }).extend({ validate: () => this.link.validateRoute() });
        this.topicId = this.link.topicId;

        const topicsManager = storage.get(SimpleTopicsManager, { discussionId });
        const topicsReady = topicsManager.pluck(m => !m.loading()).extend({ deferred: true });
        this.groups = topicsManager.pluck(m => m.groups, []);
        this.topics = topicsManager.pluck(m => m.topics, []);

        this.hasGroups = this.groups.is(groups => groups.length > 1);
        this.hasNoGroups = this.hasGroups.not();
        this.hasTopics = this.topics.is(topics => topics.length > 0);

        this.cssEditor = {
            value: this.css,
            placeholder: 'CSS',
            theme: 'underline',
            onKeyDown: this.onKeyDown.bind(this)
        };

        this.titleEditor = {
            value: this.title,
            placeholder: 'Title',
            theme: 'underline',
            onKeyDown: this.onKeyDown.bind(this)
        };

        this.accessSelect = new MultiSelectDropdown({
            defaultTitle: 'No Restrictions',
            items: [
                { title: 'Moderators', value: enums.MembershipStatusTypes.Moderator.value },
                { title: 'Observers', value: enums.MembershipStatusTypes.Observer.value }
            ]
        });
        
        this.typeSelect = new SelectDropdown({
            items: ko.pureComputed(() => this.getTypeSelectItems())
        });

        this.routeEditor = {
            value: this.route,
            placeholder: 'Route',
            theme: 'underline',
            onKeyDown: this.onKeyDown.bind(this)
        };

        this.showRouteEditor = this.type.is(CommunityNavigationWidgetLinkType.url);
        this.showTopicGroupsList = this.type.is(CommunityNavigationWidgetLinkType.activitiesGroup);
        this.showTopicsList = this.type.is(CommunityNavigationWidgetLinkType.activity);
        this.allowToSetAccess = ko.pureComputed(() => this.checkIfSupportsAccessSettings());

        const ready = ko.pureComputed(() => topicsReady());        

        effects.register(ready => {
            if (ready) {
                this.typeSelect.select(item => item.value == this.type());
                this.link.accessRestrictions().forEach(restriction => this.accessSelect.select(item => item.value == restriction));

                return [
                    this.typeSelect.selectedItem.subscribeNotNull(item => {
                        this.route('');
                        this.topicId(undefined);

                        this.pickedGroup(undefined);
                        this.pickedTopic(undefined);

                        this.type(item.value);
                        this.accessSelect.clear();

                        if (item.value == CommunityNavigationWidgetLinkType.moderatorTools)
                            this.accessSelect.select(item => item.value == enums.MembershipStatusTypes.Moderator.value);

                        if (item.value == CommunityNavigationWidgetLinkType.observerTools)
                            this.accessSelect.select(item => item.value == enums.MembershipStatusTypes.Observer.value);
                    }),

                    withEffect(items => {
                        this.link.accessRestrictions(items.map(item => item.value));
                    }, [this.accessSelect.selectedItems]),

                    withEffect((pickedGroup, groups, topicId, type) => {
                        if (pickedGroup == undefined && type == CommunityNavigationWidgetLinkType.activitiesGroup) {
                            pickedGroup = groups.find(group => group.id() == topicId) ??
                                groups.find(group => group.topics().some(topic => topic.id() == topicId)) ??
                                groups[0];

                            this.pickedGroup(pickedGroup);
                        }
                    }, [this.pickedGroup, this.groups, this.topicId, this.type]),

                    withEffect(group => {
                        if (group != undefined && this.type() === CommunityNavigationWidgetLinkType.activitiesGroup)
                            this.topicId(group.id());
                    }, [this.pickedGroup]),

                    withEffect((pickedTopic, topics, topicId, type) => {
                        if (pickedTopic == undefined && type == CommunityNavigationWidgetLinkType.activity) {
                            pickedTopic = topics.find(topic => topic.id() == topicId) ?? topics[0];

                            this.pickedTopic(pickedTopic);
                        }
                    }, [this.pickedTopic, this.topics, this.topicId, this.type]),

                    withEffect(topic => {
                        if (topic != undefined && this.type() == CommunityNavigationWidgetLinkType.activity) {
                            this.topicId(topic.id());
                        }
                    }, [this.pickedTopic])
                ]
            }
        }, [ready]);        

        ko.applyBindingsToNode(info.element, {
            'css': {
                'community__navigation__layout__link': true
            }
        });
    }

    moveUp = () => {
        this.config.onMove?.(this.link, -1);
    }

    moveDown = () => {
        this.config.onMove?.(this.link, 1);
    }

    remove = () => {
        system.confirm('Are you sure you want to delete this link?', () => {
            this.config.onRemove?.(this.link);
        });
    }

    private checkIfSupportsAccessSettings() {
        switch (this.type()) {
            case CommunityNavigationWidgetLinkType.moderatorTools:
            case CommunityNavigationWidgetLinkType.observerTools:
                return false;

            default:
                return !this.isPublic();
        }
    }

    private getTypeSelectItems() {
        const result: Array<DropdownItem<number>> = [
            { title: 'URL', value: CommunityNavigationWidgetLinkType.url }
        ]

        if (!this.isPublic()) {
            result.push({ title: 'Activities', value: CommunityNavigationWidgetLinkType.activities });

            if (this.hasGroups())
                result.push({ title: 'Activities Group', value: CommunityNavigationWidgetLinkType.activitiesGroup });

            if (this.hasTopics())
                result.push({ title: 'Activity', value: CommunityNavigationWidgetLinkType.activity });

            result.push({ title: 'To Do', value: CommunityNavigationWidgetLinkType.todo });

            result.push(
                { title: 'Moderator Tools', value: CommunityNavigationWidgetLinkType.moderatorTools },
                { title: 'Observer Tools', value: CommunityNavigationWidgetLinkType.observerTools }
            );
        }

        return result;
    }

    private onKeyDown(e: JQueryEventObject) {
        switch (e.keyCode) {
            case 13: return false;
            default: return true;
        }
    }
}